import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useRecipe, useUnblockRecipe } from '../api/recipes';
import ConfirmDialog from '../components/ConfirmDialog';
import CookingTime from '../components/CookingTime';
import RecipeThumb from '../components/RecipeThumb';
import StepViewer from '../components/StepViewer';

const SEASON_LABEL: Record<string, string> = {
  spring: '🌱 Spring',
  summer: '☀️ Summer',
  fall: '🍂 Fall',
  winter: '❄️ Winter',
};

/** What to call the way back, given where it goes.
 *
 * A recipe is opened from the week, from a past week, and from the corpus
 * browser, and the link used to say "Recipes" for all three — so following it
 * from the plan dropped you into 4,800 cards with your week gone.
 */
function backLabel(path: string): string {
  if (path === '/') return 'This week';
  if (path.startsWith('/history')) return 'Past weeks';
  if (path.startsWith('/list')) return 'Shopping list';
  return 'Recipes';
}

export default function RecipeDetail() {
  const { id } = useParams();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/recipes';
  const [servings, setServings] = useState<number | undefined>(undefined);
  const [cooking, setCooking] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const { data: recipe, isLoading, error } = useRecipe(id, servings);
  const unblock = useUnblockRecipe();

  if (isLoading) return <p className="center-note">Loading recipe…</p>;
  if (error || !recipe) return <p className="center-note">Couldn’t load that recipe.</p>;

  if (cooking && (recipe.steps.length > 0 || recipe.panels.length > 0)) {
    return (
      <StepViewer
        recipeId={recipe.id}
        steps={recipe.steps}
        panels={recipe.panels}
        ingredients={recipe.ingredients}
        servings={recipe.scaled_to}
        onClose={() => setCooking(false)}
      />
    );
  }

  return (
    <article>
      <Link to={from} className="back-link">
        ← {backLabel(from)}
      </Link>
      <div className="recipe-head">
        <RecipeThumb id={recipe.id} title={recipe.title} size="lg" />
        <h1>{recipe.title}</h1>
      </div>

      {recipe.swap_of && (
        <p className="muted small">
          The {recipe.swap_label} version of{' '}
          <Link to={`/recipes/${recipe.swap_of.id}`}>{recipe.swap_of.title}</Link>
        </p>
      )}

      <p className="meta">
        {recipe.season && <span>{SEASON_LABEL[recipe.season] ?? recipe.season}</span>}
        <CookingTime recipe={recipe} />
        {recipe.estimated_calories && <span>{recipe.estimated_calories} cal</span>}
        {recipe.proteins.map((p) => (
          <span key={p} className="chip">
            {p}
          </span>
        ))}
      </p>

      {/* The way back from a box ticked in the middle of planning a week.
          Above the cooking controls because it is about whether this dinner
          gets offered, not about making it -- a blocked recipe is still
          perfectly cookable today. */}
      {recipe.blocked && (
        <div className="card notice notice--never-again">
          <p>
            You asked not to be offered this again
            {recipe.block_reason && <>: “{recipe.block_reason}”</>}
          </p>
          <button
            className="btn btn--small"
            disabled={unblock.isPending}
            onClick={() => setUnblocking(true)}
          >
            {unblock.isPending ? 'Unblocking…' : 'Unblock'}
          </button>
        </div>
      )}

      {unblocking && (
        <ConfirmDialog
          titleId="confirm-unblock-title"
          title={`Offer ${recipe.title} again?`}
          onCancel={() => setUnblocking(false)}
          actions={
            <>
              <button
                className="btn btn--primary"
                disabled={unblock.isPending}
                onClick={() => {
                  unblock.mutate(recipe.id);
                  setUnblocking(false);
                }}
              >
                Offer it again
              </button>
              <button className="link-button" onClick={() => setUnblocking(false)}>
                Leave it blocked
              </button>
            </>
          }
        >
          <p className="muted small">
            It goes back in the running for future weeks. It won’t appear on a
            week that has already been chosen.
          </p>
        </ConfirmDialog>
      )}

      <div className="servings-row">
        <span className="muted">Scale to</span>
        {[2, 4, 6].map((count) => (
          <button
            key={count}
            className={`btn btn--small ${recipe.scaled_to === count ? 'is-active' : ''}`}
            onClick={() => setServings(count)}
          >
            {count}
          </button>
        ))}
        <span className="muted">servings</span>
      </div>

      {/* Which amounts were printed and which are arithmetic. Cards come at 2
          or 4 servings, a few at 3, and scaling off an odd one is worth
          knowing about before you trust a half-egg. */}
      {recipe.scaled_to !== recipe.servings && (
        <p className="muted small">
          The card is written for {recipe.servings} servings; these amounts are
          scaled from it.
        </p>
      )}

      {recipe.steps.length > 0 ? (
        <button className="btn btn--primary btn--wide" onClick={() => setCooking(true)}>
          Start cooking →
        </button>
      ) : (
        <p className="muted card">
          Steps for this recipe haven’t been extracted yet. The original card is still
          available below.
        </p>
      )}

      <h2>Ingredients</h2>
      <ul className="ingredients">
        {recipe.ingredients.length > 0
          ? recipe.ingredients.map((item) => (
              <li key={item.ingredient_id}>
                <span className="ingredients__amount">
                  {item.quantity_display} {item.unit}
                </span>
                <span>{item.name}</span>
              </li>
            ))
          : recipe.raw_ingredients.map((line) => (
              <li key={line}>
                <span>{line}</span>
              </li>
            ))}
      </ul>

      {recipe.variants.length > 0 && (
        <>
          <h2>Variations</h2>
          <ul className="variants">
            {recipe.variants.map((variant) => (
              <li key={variant.id}>
                <Link to={`/recipes/${variant.id}`}>{variant.title}</Link>
                {variant.swap_label && <span className="chip">{variant.swap_label}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      <h2>Original card</h2>
      {/* A page of our own rather than a new tab: in the installed app a new
          tab has no chrome to come back with. */}
      <Link className="btn btn--wide" to={`/recipes/${recipe.id}/card`}>
        Open recipe card (PDF)
      </Link>
    </article>
  );
}
